import { useState, useCallback } from 'react'
import { paymentsApi } from '../../services/api'
import { openRazorpayCheckout } from '../../utils/razorpay'
import { extractErrorMessage } from '../../utils/errorUtils'
import { trackInitiateCheckoutEvent, trackSubscribeEvent } from '../../utils/adsLeadAttributionUtils'

interface UsePaymentCheckoutOptions {
  onSuccess?: () => void
  showNotification: (message: string, severity: 'success' | 'error' | 'info' | 'warning') => void
}

export function usePaymentCheckout({ onSuccess, showNotification }: UsePaymentCheckoutOptions) {
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [pollingStatus, setPollingStatus] = useState<string | null>(null)

  const pollPaymentStatus = useCallback(
    async (transactionId: string, successMessage: string) => {
      setPollingStatus('Please hold on while we confirm your transaction.')
      let attempts = 0

      const pollInterval = setInterval(async () => {
        attempts += 1
        try {
          const statusRes = await paymentsApi.getPaymentStatus(transactionId)
          if (statusRes.status === 'paid' || statusRes.status === 'active') {
            clearInterval(pollInterval)
            trackSubscribeEvent()
            if (onSuccess) await onSuccess()
            showNotification(successMessage, 'success')
            setPollingStatus(null)
            setCheckoutLoading(false)
          } else if (attempts > 60) {
            clearInterval(pollInterval)
            if (onSuccess) await onSuccess()
            setPollingStatus(null)
            setCheckoutLoading(false)
          }
        } catch {
          clearInterval(pollInterval)
          setPollingStatus(null)
          setCheckoutLoading(false)
        }
      }, 2000)
    },
    [onSuccess, showNotification]
  )

  const selectPlan = useCallback(
    async (smbId: string, planType: string) => {
      setCheckoutLoading(true)
      try {
        const checkoutData = await paymentsApi.createPayment(smbId, {
          payment_type: 'subscription',
          plan_type: planType,
          billing_months: 1,
        })
        
        trackInitiateCheckoutEvent()

        const checkoutResult = await openRazorpayCheckout(checkoutData)
        if (checkoutResult.isAbandon) {
          paymentsApi.abandonPayment(checkoutData.transaction_id).catch(() => {})
          setCheckoutLoading(false)
          return
        }

        await pollPaymentStatus(checkoutData.transaction_id, 'Payment successful! Subscription updated.')
      } catch (err: any) {
        console.error('Checkout error:', err)
        setPollingStatus(null)
        setCheckoutLoading(false)
        const msg = extractErrorMessage(err, 'Failed to initiate checkout.')
        showNotification(msg, 'error')
      }
    },
    [pollPaymentStatus, showNotification]
  )

  const purchaseTopUp = useCallback(
    async (smbId: string, amount: number, units: number) => {
      setCheckoutLoading(true)
      try {
        const checkoutData = await paymentsApi.createPayment(smbId, {
          payment_type: 'topup',
          amount_subunits: amount * 100,
          topup_units: units,
        })

        const checkoutResult = await openRazorpayCheckout(checkoutData)
        if (checkoutResult.isAbandon) {
          try {
            await paymentsApi.abandonPayment(checkoutData.transaction_id)
          } catch {
            // silent ignore
          }
          setCheckoutLoading(false)
          return
        }

        showNotification('Processing top-up payment...', 'info')
        await pollPaymentStatus(
          checkoutData.transaction_id,
          `Top-Up Successful! Added ${units.toLocaleString()} AI Replies.`
        )
      } catch (err: any) {
        console.error('Topup checkout error:', err)
        setCheckoutLoading(false)
        const msg = extractErrorMessage(err, 'Failed to process top-up checkout.')
        showNotification(msg, 'error')
      }
    },
    [pollPaymentStatus, showNotification]
  )

  const cancelSubscription = useCallback(
    async (smbId: string, subscriptionId?: string) => {
      setCheckoutLoading(true)
      try {
        await paymentsApi.cancelSubscription(smbId, subscriptionId)
        showNotification('Auto-renewal cancelled successfully.', 'success')
        if (onSuccess) onSuccess()
      } catch (err: any) {
        console.error('Failed to cancel subscription:', err)
        const msg = extractErrorMessage(err, 'Failed to cancel subscription.')
        showNotification(msg, 'error')
      } finally {
        setCheckoutLoading(false)
      }
    },
    [onSuccess, showNotification]
  )

  return {
    checkoutLoading,
    pollingStatus,
    selectPlan,
    purchaseTopUp,
    cancelSubscription,
  }
}
